import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {DishesService} from "app/services/dishes.service";
import {TranslateService} from "@ngx-translate/core";
import {DomSanitizer} from "@angular/platform-browser";
import {UploadVideoRequest} from "app/models/menu.model";
import {take} from "rxjs";

// Of all the video containers a browser will happily hand us (mov, webm, avi,
// mkv…) the backend only accepts mp4 — everything else used to be uploaded
// silently and blew up server-side. Images stay allowed: the field doubles as
// a picture picker, see isVideo() and the <img> branch of the template.
const AllowedFormats = ['mp4', 'jpg', 'jpeg', 'png'];

@Component({
  selector: 'app-select-video-field',
  templateUrl: './select-video-field.component.html',
  styleUrls: ['./select-video-field.component.scss']
})
export class SelectVideoFieldComponent implements OnInit {
  reader = new FileReader();
  mediaDefaultSrc = '';
  selectedMediaSrc;

  @Input() allowOverrides: boolean;
  @Input() video: string;
  @Input() organizationId: string;
  @Input() overrideUrl: string;
  @Input() inputVideoDefaultSrc: string;
  @Output() videoChanged = new EventEmitter<string>();

  constructor(
    private dishesService: DishesService,
    private translate: TranslateService,
    private sanitizer: DomSanitizer
  ) {
  }

  ngOnInit(): void {
    this.mediaDefaultSrc = this.inputVideoDefaultSrc ?? this.mediaDefaultSrc;
    this.selectedMediaSrc = this.video ? this.video : this.mediaDefaultSrc;
    this.reader.onload = (e: any) => {
      const base64String = e.target.result
        .replace('data:', '')
        .replace(/^.+,/, '');
      const format = this.getMediaTypeFromBase64(e.target.result);
      if (!format) {
        // the user has already been alerted — don't upload a nameless blob
        return;
      }

      const uploadVideoRequest: UploadVideoRequest = {
        organizationId: this.organizationId,
        videoBase64: base64String,
        IsDeletedPicture: false,
        format: format,
      };

      if (this.overrideUrl) {
        uploadVideoRequest.videoUrl = this.overrideUrl;
      }

      this.dishesService.uploadVideo$(uploadVideoRequest)
        .pipe(take(1))
        .subscribe(res => {
          const videoUrl = res.data;
          this.selectedMediaSrc = videoUrl;
          this.videoChanged.emit(videoUrl);
        });
    };
  }

  changeVideo(event): void {
    if (!this.allowOverrides) {
      return;
    }

    const file: File = event.target.files && event.target.files[ 0 ];
    if (!file) {
      return;
    }

    if (!this.isAllowedFile(file)) {
      alert(this.translate.instant('MENU.ALERT_MEDIA'));
      // clear the input, otherwise picking the very same file again after a
      // rejection fires no change event and the field looks frozen
      event.target.value = '';
      return;
    }

    this.reader.readAsDataURL(file);
  }

  private isAllowedFile(file: File): boolean {
    // the extension is what the user actually sees and what the backend keys
    // off; file.type only covers the case of a name without an extension
    const format = (this.getMediaTypeByUrl(file.name) || file.type.split('/')[ 1 ] || '').toLowerCase();

    return AllowedFormats.includes(format);
  }

  deleteDishVideo(): void {
    if (!this.allowOverrides) {
      return;
    }
    this.selectedMediaSrc = this.mediaDefaultSrc;
    this.videoChanged.emit('');
  }

  getMediaTypeFromBase64(base64String) {
    if (!base64String) {
      return null;
    }
    const pattern = 'data:(image|video)/([a-zA-Z0-9]+)';
    const regexp = new RegExp(pattern);
    const match = base64String.match(regexp);
    // second line of defence: changeVideo() already filtered by extension, this
    // catches a file whose real content type disagrees with its name
    if (match && match.length >= 3 && AllowedFormats.includes(match[ 2 ].toLowerCase())) {
      return match[ 2 ];
    } else {
      alert(this.translate.instant('MENU.ALERT_MEDIA'));
      return null;
    }
  }

  isVideo(format: string): boolean {

    switch (format) {
      case 'mp4':
        return true;
      case 'jpeg':
      case 'jpg':
      case 'png':
        return false;
      case null:
        return null;
    }
  }

  getMediaTypeByUrl(url: string) {
    if (!url || typeof url !== 'string') {
      return null;
    }
    return url.substring(url.lastIndexOf('.') + 1);
  }
}
